import React, { useState } from 'react';

/**
 * A dropdown that keeps its trigger looking like the chart and opens the app's
 * own menu instead of the browser's.
 *
 * The trigger takes whatever class and style its caller gives it, so a value
 * that is drawn as part of the music — the time signature — goes on looking
 * like music. The menu matches the Key Center panel's picker and the
 * instrument and MIDI menus: #1a1a1a panel, faint white border, the current
 * value held bright.
 */

export type ChartDropdownOption = {
  value: string;
  label: React.ReactNode;
};

export type ChartDropdownProps = {
  value: string;
  options: ChartDropdownOption[];
  onChange: (value: string) => void;
  title?: string;
  /** Applied to the trigger, so it can be styled as the chart styles it. */
  className?: string;
  style?: React.CSSProperties;
  /** Menu text size. Defaults to the trigger's. */
  menuFontSize?: string;
};

const ChartDropdown: React.FC<ChartDropdownProps> = ({
  value, options, onChange, title, className, style, menuFontSize,
}) => {
  const [open, setOpen] = useState(false);
  const current = options.filter(option => option.value === value)[0];

  // As wide as the longest value it can hold, which is how a <select> sizes
  // itself and what keeps the chart's spacing steady as the value changes.
  const widest = options.reduce((widthSoFar, option) => Math.max(widthSoFar, option.value.length), 1);

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <span
        data-actionable="true"
        title={title}
        className={className}
        style={{
          display: 'inline-block',
          minWidth: `${widest}ch`,
          textAlign: 'center',
          ...style,
          cursor: 'pointer',
        }}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
      >
        {current === undefined ? value : current.label}
      </span>

      {open && (
        <>
          <div
            data-actionable="true"
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
            onClick={() => setOpen(false)}
          />
          <div
            data-actionable="true"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '0.3dvw',
              backgroundColor: '#1a1a1a',
              border: '0.05dvw solid rgba(255, 255, 255, 0.1)',
              borderRadius: '0.3dvw',
              padding: '0.3dvw 0',
              minWidth: '2dvw',
              zIndex: 1000,
              boxShadow: '0 0.2dvw 0.5dvw rgba(0, 0, 0, 0.3)',
              textShadow: 'none',
              whiteSpace: 'nowrap',
              // The chart sets a line-height of about 3dvw for its chords; a
              // menu row is a row of text.
              lineHeight: 1.4,
              fontSize: menuFontSize,
            }}
          >
            {options.map(option => {
              const isCurrent = option.value === value;
              return (
                <div
                  key={option.value}
                  data-actionable="true"
                  onClick={(e) => { e.stopPropagation(); onChange(option.value); setOpen(false); }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isCurrent ? 'rgba(255, 255, 255, 0.1)' : 'transparent';
                  }}
                  style={{
                    padding: '0.2dvw 0.6dvw',
                    cursor: 'pointer',
                    textAlign: 'center',
                    color: isCurrent ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                    fontWeight: isCurrent ? 'bold' : 'normal',
                    backgroundColor: isCurrent ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  }}
                >
                  {option.label}
                </div>
              );
            })}
          </div>
        </>
      )}
    </span>
  );
};

export default ChartDropdown;
